// The cloud map's 1 km pages: NASA's Blue Marble clouds (the image the 8K
// earth_clouds.jpg is built from, src/data/cloudImagerySource.js) streamed
// through the body-generic imagery streamer (planetimagery.js) as one R8
// atlas, for both cloud consumers: the app's globe (src/shaders/earth.js) and
// rocketry's shell over the terrain patch (orbitclouds.js). Both read the map
// through cloudMapRead below, so the two agree at the patch rim.
//
// The pages lie in the map's own drifted address (earthweather.js: the
// reference is warped and carried in latitude-dependent winds), so the 1 km
// detail stays locked to the 8K base and moves with it. They are requested
// around the ground the drifted map lies over (the streamer's shift), which
// is where the camera looks.
//
// A page and the 8K are one image at two resolutions, so they agree in the
// mean at every footprint; where a page is resident it replaces the 8K read
// (faded in by the streamer, parent to child, never popping). Only the map
// value changes: the cells the carve cuts it from (earthcloudtop.js), the
// regime, the brokenness and the morphology are all taken from the 8K, so a
// page arriving sharpens the cloud outlines and never re-deals the cells.
// Pages coarser than the 8K (the root levels, kept for the far view's
// coverage) give way to it.
import * as THREE from 'three';
import { createPlanetImagery } from './planetimagery.js';
import { CLOUD_IMAGERY_CACHE, CLOUD_IMAGERY_DATASET, CLOUD_IMAGERY_GRID, CLOUD_IMAGERY_OFFSET, cloudImageryUrl } from '../data/cloudImagerySource.js';
import { earthWeatherDriftCPU } from '../shaders/earthweather.js';
import { earthCameraFrame } from '../earthclose.js';
import { RUNNING_ON_MOBILE } from '../mobiledevice.js';

const EARTH_R = 6371000;
// Above this the selection only reaches levels 3 and 4, which the blend
// weights at nothing and at most a sixth (cloudPagedBlend), so nothing is
// requested; what is resident stays (the far view's mean is the same either
// way).
const STREAM_ALTITUDE = 4.0e6;
// Leaves in the selection: a view from 150 to 2000 km takes 3 to 45. The
// atlas holds three times that (144 tiles, one channel: 50 MB with its
// mips), so the pages a drag has just left stay resident and dragging back
// finds them. A phone, with a sixth of the pixels, takes 24 into twice that
// (49 tiles, 17 MB; docs/mobile-memory.md).
const BUDGET = RUNNING_ON_MOBILE ? 24 : 48;
const ATLAS_SIDE = RUNNING_ON_MOBILE ? 7 : 12;
// The ring past the edge of the view that is requested too (planetimagery.js
// prefetch: the view 1.3 times as wide each way), so the orbit's own motion
// finds the pages already loaded: from low orbit the ring leads the view by
// tens of kilometres, several seconds at orbital speed, and a page takes a
// fraction of a second to arrive. It narrows from 600 to 1500 km up, where the view
// alone comes near the budget. A page that does arrive on
// screen still fades in (IMAGERY_FADE_SECONDS in planetimagery.js; at
// 400 km the frame settles about 1.6 s after the page lands), which reads
// as the 1 km data sharpening the outlines the 8K drew: the cells the carve
// cuts stay as they were.
const PREFETCH = [1.3, 600e3, 1500e3];

const clouds = createPlanetImagery({
  name: 'clouds', radius: EARTH_R, ...CLOUD_IMAGERY_GRID, atlasSide: ATLAS_SIDE,
  linear: true, channels: 1, bspline: true,
  cacheName: CLOUD_IMAGERY_CACHE, localUrl: cloudImageryUrl, url: cloudImageryUrl
});

// A rebuilt tile set is a new cache (cloudImagerySource.js); the tiles of an
// older build are dropped from the browser's storage.
try {
  globalThis.caches?.keys().then((names) => {
    for (const n of names) if (n.startsWith(`clouds-${CLOUD_IMAGERY_DATASET}-`) && n !== CLOUD_IMAGERY_CACHE) caches.delete(n);
  }).catch(() => {});
} catch { /* no Cache Storage here */ }

export const cloudImageryUniforms = clouds.uniforms;
export const getCloudImageryStream = clouds.getStream;

const f = (v) => v.toFixed(8);
// Radians one texel of a page-level tile spans, and pages per radian.
const PAGE_TEXEL = clouds.PAGE_SPAN * Math.PI / 180 / clouds.TILE_SIZE;
const PAGES_PER_RAD = clouds.PAGE_HEIGHT / Math.PI;
// Include after EARTH_WEATHER_UV_GLSL and EARTH_CLOUD_TOP_GLSL.
export const CLOUD_IMAGERY_GLSL = /* glsl */`
${clouds.glsl}
const float CLOUD_PAGE_TEXEL = ${f(PAGE_TEXEL)};
const float CLOUD_PAGES_PER_RAD = ${f(PAGES_PER_RAD)};
const float CLOUD_PAGE_OFFSET = ${CLOUD_IMAGERY_OFFSET.toFixed(1)};

struct CloudMapRead {
  float m;      // the map's cover, the 8K's units
  float page;   // how much of m is the streamed page
  float m8;     // the 8K's own read
};

// The 8K read m8 (texel mapM metres) under the page sample s (premultiplied
// by its weight, cells premultiplied the same way).
CloudMapRead cloudPagedBlend(float m8, float mapM, vec4 s, float cells) {
  CloudMapRead r;
  r.m = m8;
  r.m8 = m8;
  r.page = 0.0;
  if (s.a > 1e-4) {
    float pageM = CLOUD_PAGE_TEXEL * CT_RADIUS * cells / s.a;
    // Only a page finer than the 8K is worth reading: the root levels are
    // there for coverage, and the 8K is sharper than them.
    r.page = s.a * smoothstep(1.0, 2.0, mapM / pageM);
    // Stored with an offset so clear sky is never 0 (the streamer reads 0 as
    // no imagery); decoding is linear, so it commutes with the filtering.
    float v = (s.r / s.a * 255.0 - CLOUD_PAGE_OFFSET) / (255.0 - CLOUD_PAGE_OFFSET);
    r.m = clamp(mix(m8, v, r.page), 0.0, 1.0);
  }
  return r;
}

// The cloud map at uv (the drifted address) as the globe and the shell read
// it: the 8K through earthCloudCover, the resident pages over it.
CloudMapRead cloudMapRead(sampler2D map, vec2 uv, vec2 texel, float sharpen) {
  // The pages' footprint, from the unwrapped address and before any branch.
  vec2 raw = uv * vec2(${f(clouds.PAGE_WIDTH)}, -${f(clouds.PAGE_HEIGHT)});
  vec2 dx = dFdx(raw), dy = dFdy(raw);
  float m8 = earthCloudCover(map, uv, texel, sharpen);
  float cells;
  vec4 s = cloudsImagerySampleGrad(uv, vec3(0.0), dx, dy, cells);
  return cloudPagedBlend(m8, texel.y * 3.14159265 * CT_RADIUS, s, cells);
}

// cloudMapPoint (earthcloudtop.js) over a paged read: the cover the carve
// splits is the page's, everything else the 8K's.
CloudMapPoint cloudPagedMapPoint(sampler2D map, vec2 uv, vec2 texel, CloudMapRead r, float lat, float opacity) {
  return cloudMapPoint(map, uv, texel, r.m, r.m8, lat, opacity);
}

// The same for a footprint of footM metres at latitude lat, with no screen
// derivatives, for reads inside branches (the ground-shadow probes).
CloudMapRead cloudMapReadLod(sampler2D map, vec2 uv, float footM, float lat, float mapM) {
  float m8 = textureLod(map, uv, clamp(log2(footM / mapM), 0.0, 10.0)).r;
  float d = footM / CT_RADIUS * CLOUD_PAGES_PER_RAD;
  float cells;
  vec4 s = cloudsImagerySampleGrad(uv, vec3(0.0), vec2(d / max(cos(lat), 0.02), 0.0), vec2(0.0, d), cells);
  return cloudPagedBlend(m8, mapM, s, cells);
}

// How much of the direct sun the cloud keeps off the ground at Ng (drifted
// address): probed along the sun at low-cloud and high-cloud height, each
// probe the carve (earthcloudtop.js) at the coarser bands, read at the
// pixel's own footprint through the pages, so a shadow falls from the cloud
// drawn above it. Each cumulus and its shadow beside it, as ISS and Starship
// frames show them: read two pixels coarse, the shadows of a scattered field
// merged into a grey haze over the sea once the shadow took the water's
// direct light (cloudTopShadowStrength). c is the pixel's own map point, for the regime, texture and type near
// by; mapM is the 8K's texel in metres. The map is read with explicit
// footprints: the probes sit in branches.
float cloudPagedGroundShadow(sampler2D map, vec3 Ng, vec3 sT, float pixM, CloudMapPoint c, float opacity, float mapM) {
  if (sT.z < 0.02) return 0.0;
  float horiz = max(length(sT.xy), 1e-4);
  vec2 h = sT.xy / horiz;
  float tanE = max(sT.z / horiz, 0.0875);
  float pixP = max(pixM, 300.0);
  float shadow = 0.0;
  int probes = uCloudTopQuality > 0.5 ? 2 : 1;
  for (int i = 0; i < 2; i++) {
    if (i >= probes) break;
    float hgt = i == 0 ? 1800.0 : 9500.0;
    vec3 Np = cloudTopOffset(Ng, h * (hgt / tanE));
    CloudMapPoint p = c;
    CloudMapRead r = cloudMapReadLod(map, cloudTopUv(Np), pixP, asin(clamp(Np.y, -1.0, 1.0)), mapM);
    p.m = r.m;
    p.m8 = r.m8;
    cloudMapSplit(p, opacity);
    // The cirrus at its own height (earthcloudtop.js): a faint shadow, as
    // dark as the streak is opaque, also over clear sea.
    if (i == 1) shadow = max(shadow, 0.85 * ctCirrusAlpha(Np, p, pixP, ctCirrusMean(Np, p)));
    if (p.alpha < 0.002) continue;
    CloudTop t = cloudTopField(Np, p, pixP, i == 0 && probes > 1 ? 2 : 1);
    float s = min(t.cover * 0.8 + t.core * 0.2, 1.0);
    shadow = max(shadow, s * (i == 0 ? 1.0 - t.high : t.high));
  }
  return shadow * smoothstep(0.02, 0.25, sT.z);
}
`;

// The map's ground offset at latitude lat (radians) and weather time t: the
// ground under map longitude lon is at lon + shift(lat).
function driftShift(t) { return (lat) => 2 * Math.PI * earthWeatherDriftCPU(lat, t); }

// One selection and upload step for the camera at position (metres, Earth's
// surface-fixed frame), looking along orientation through lens.
export function updateCloudImagery(renderer, { now, position, orientation, lens, weatherTime = 0 }) {
  const stream = clouds.getStream(renderer);
  const altitude = position.length() - EARTH_R;
  if (altitude > STREAM_ALTITUDE) {
    // A phone frees the atlas well away from the Earth (as earthclose.js).
    if (RUNNING_ON_MOBILE && altitude > 8 * EARTH_R) stream.release();
    return stream;
  }
  const prefetch = 1 + (PREFETCH[0] - 1) * (1 - THREE.MathUtils.smoothstep(altitude, PREFETCH[1], PREFETCH[2]));
  stream.update({ now, position, orientation, lens, budget: BUDGET, shift: driftShift(weatherTime), prefetch });
  return stream;
}

// The normal viewer: the camera in the Earth mesh's frame, at either scale.
const _position = new THREE.Vector3(), _orientation = new THREE.Quaternion();
export function updateViewerCloudImagery(renderer, mesh, camera, now) {
  const u = mesh?.material?.uniforms;
  if (!u?.uCloudsAtlas || !mesh.geometry?.parameters?.radius) return null;
  earthCameraFrame(mesh, camera, _position, _orientation);
  return updateCloudImagery(renderer, { now, position: _position, orientation: _orientation,
    lens: camera.projectionMatrix, weatherTime: u.weatherTime?.value || 0 });
}

// Whether o and every parent of it are visible.
function shown(o) {
  for (; o; o = o.parent) if (!o.visible) return false;
  return true;
}

// Rocketry: the flight camera over the true-scale Earth. The pages are never
// cancelled on the way out; the viewer carries on with the same atlas. Only
// requested while a reader draws (the globe beyond the patch, the orbital
// shell over it, or the volume carving the tops' cells from 30 km up): on
// the pad and through the low ascent none does, and the atlas (50 MB) is
// allocated on the first upload.
export function createGlobalCloudImagery(G) {
  const stream = getCloudImageryStream(G.ctx.renderer);
  const position = new THREE.Vector3(), spin = new THREE.Quaternion(), orientation = new THREE.Quaternion();
  const off = G.events.on('frame', ({ now }) => {
    if (!G.R.active || G.R.viewBody !== 'earth' || G.R.mode === 'vab') return;
    const globe = G.bodies.get('earth').spinMesh;
    if (!shown(globe) && !(G.orbitClouds && G.orbitClouds.drawn()) && !(G.volClouds && G.volClouds.topsActive())) return;
    const p = G.bodies.inertialToSurface('earth', G.camInertial);
    position.set(p.x, p.y, p.z);
    G.bodies.spinQuaternion('earth', spin).invert();
    orientation.copy(spin).multiply(G.localCamera.quaternion);
    const u = G.bodies.get('earth').spinMesh?.material?.uniforms;
    updateCloudImagery(G.ctx.renderer, { now, position, orientation, lens: G.localCamera.projectionMatrix,
      weatherTime: u?.weatherTime?.value || 0 });
  });
  G.cloudImagery = stream;
  return { stats: stream.stats, dispose() { off(); } };
}
